import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  LogOut, Menu, ShoppingCart, FileText, Image as ImageIcon,
  DollarSign, Layers, Film, BarChart3, Search, Globe, Activity, Swords,
} from "lucide-react";
import { toast } from "sonner";

import FoilPricingSettings from "@/components/cms/FoilPricingSettings";
import FoilQuantityPricingSettings from "@/components/cms/FoilQuantityPricingSettings";
import FoilVideoSettings from "@/components/cms/FoilVideoSettings";
import OrdersList from "@/components/cms/OrdersList";
import AnalyticsDashboard from "@/components/cms/AnalyticsDashboard";
import GalleryManager from "@/components/cms/GalleryManager";
import ArticlesPanel from "@/components/cms/ArticlesPanel";
import SeoTips from "@/components/cms/SeoTips";
import GscPanel from "@/components/cms/GscPanel";
import WebVitalsPanel from "@/components/cms/WebVitalsPanel";
import CompetitorPanel from "@/components/cms/CompetitorPanel";

type TabKey =
  | "orders" | "articles" | "gallery"
  | "foil-pricing" | "foil-quantity" | "foil-media"
  | "analytics" | "seo" | "gsc" | "vitals" | "competitors";

const NAV: { key: TabKey; label: string; icon: any; group: string }[] = [
  { key: "orders", label: "Zamówienia", icon: ShoppingCart, group: "Sprzedaż" },
  { key: "analytics", label: "Statystyki", icon: BarChart3, group: "Sprzedaż" },
  { key: "articles", label: "Artykuły / Blog", icon: FileText, group: "Treść" },
  { key: "gallery", label: "Galeria", icon: ImageIcon, group: "Treść" },
  { key: "seo", label: "SEO i pozycjonowanie", icon: Search, group: "Treść" },
  { key: "competitors", label: "Konkurencja (Semrush)", icon: Swords, group: "Treść" },
  { key: "gsc", label: "Search Console", icon: Globe, group: "Treść" },
  { key: "vitals", label: "Core Web Vitals", icon: Activity, group: "Treść" },
  { key: "foil-pricing", label: "Folie - cennik", icon: DollarSign, group: "Folie" },
  { key: "foil-quantity", label: "Folie - rabaty ilościowe", icon: Layers, group: "Folie" },
  { key: "foil-media", label: "Folie - media (wideo)", icon: Film, group: "Folie" },
];

export default function Cms() {
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [authLoading, setAuthLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<TabKey>("orders");
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => {
    supabase.auth.onAuthStateChange((_e, s) => { setSession(s); setLoading(false); });
    supabase.auth.getSession().then(({ data: { session } }) => { setSession(session); setLoading(false); });
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthLoading(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) toast.error(error.message);
    setAuthLoading(false);
  };

  const handleLogout = async () => { await supabase.auth.signOut(); };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-background"><p className="text-muted-foreground">Ładowanie...</p></div>;

  if (!session) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <form onSubmit={handleLogin} className="w-full max-w-sm space-y-4 bg-card border border-border rounded-xl p-8">
          <h1 className="text-2xl font-bold text-foreground text-center">CMS</h1>
          <div>
            <Label htmlFor="email">E-mail</Label>
            <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </div>
          <div>
            <Label htmlFor="password">Hasło</Label>
            <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
          </div>
          <Button type="submit" className="w-full" disabled={authLoading}>
            {authLoading ? "Logowanie..." : "Zaloguj się"}
          </Button>
        </form>
      </div>
    );
  }

  const groups = Array.from(new Set(NAV.map((n) => n.group)));
  const activeNav = NAV.find((n) => n.key === activeTab);

  const renderContent = () => {
    switch (activeTab) {
      case "orders": return <OrdersList />;
      case "articles": return <ArticlesPanel />;
      case "gallery": return <GalleryManager />;
      case "foil-pricing": return <FoilPricingSettings />;
      case "foil-quantity": return <FoilQuantityPricingSettings />;
      case "foil-media": return <FoilVideoSettings />;
      case "analytics": return <AnalyticsDashboard />;
      case "seo": return <SeoTips />;
      case "gsc": return <GscPanel />;
      case "vitals": return <WebVitalsPanel />;
      case "competitors": return <CompetitorPanel />;
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar */}
      <aside
        className={`${mobileNavOpen ? "translate-x-0" : "-translate-x-full"} md:translate-x-0
          fixed md:sticky top-0 left-0 z-30 h-screen w-64 bg-card border-r border-border
          transition-transform overflow-y-auto shrink-0`}
      >
        <div className="p-5 border-b border-border">
          <h1 className="text-xl font-bold text-foreground">CMS</h1>
          <p className="text-xs text-muted-foreground mt-1">Panel zarządzania</p>
        </div>
        <nav className="p-3 space-y-5">
          {groups.map((group) => (
            <div key={group}>
              <p className="text-xs uppercase tracking-wide text-muted-foreground px-2 mb-1.5">{group}</p>
              <div className="space-y-0.5">
                {NAV.filter((n) => n.group === group).map((item) => {
                  const Icon = item.icon;
                  const active = activeTab === item.key;
                  return (
                    <button
                      key={item.key}
                      onClick={() => { setActiveTab(item.key); setMobileNavOpen(false); }}
                      className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors text-left ${
                        active
                          ? "bg-primary text-primary-foreground font-medium"
                          : "text-foreground hover:bg-muted"
                      }`}
                    >
                      <Icon className="w-4 h-4 shrink-0" />
                      <span className="truncate">{item.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>
        <div className="p-3 mt-3 border-t border-border">
          <Button variant="ghost" size="sm" onClick={handleLogout} className="w-full justify-start">
            <LogOut className="w-4 h-4 mr-2" /> Wyloguj
          </Button>
        </div>
      </aside>

      {/* Mobile overlay */}
      {mobileNavOpen && (
        <div className="md:hidden fixed inset-0 bg-black/40 z-20" onClick={() => setMobileNavOpen(false)} />
      )}

      {/* Main */}
      <div className="flex-1 min-w-0">
        <header className="sticky top-0 z-10 bg-card/95 backdrop-blur border-b border-border">
          <div className="px-4 md:px-8 py-3 flex items-center gap-3">
            <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setMobileNavOpen(true)}>
              <Menu className="w-5 h-5" />
            </Button>
            <h2 className="text-lg font-semibold text-foreground">{activeNav?.label}</h2>
          </div>
        </header>
        <main className="p-4 md:p-8">
          {renderContent()}
        </main>
      </div>
    </div>
  );
}
